import { useState } from 'react';

import { STR } from '../../../../i18n';
import { showToast } from '../../../../lib/toast';
import { Modal } from '../../../../ui/Modal';
import { Callout } from '../../../../ui/Primitives';
import { fieldLabelStyle, hintStyle, modalButtonStyle, monoStyle, rowActionStyle } from '../../../../ui/styles';

import { IT_TONE } from './tone';
import { ColouredEmail, ToneLegend } from './ToneParts';

export interface ItEmailSummary {
  items: number;
  systems: number;
  setUp: number;
  remove: number;
  /** Items on a login other people also use: the email names them, so IT rotates. */
  shared: number;
}

/**
 * NEW EMAIL TO IT, STEP 2 — and the read view of one already sent (FINAL demo, change 1).
 * The text shows in colour; Edit text swaps in the plain box and Done editing brings the
 * colours back. Save draft keeps the text for later; Mark as sent moves the items to
 * Waiting on IT. ACCOUNTS SENDS NOTHING ITSELF: the person copies the text into their mail
 * app. A sent email is read-only here (Copy text and Close).
 */
export function ItEmailDialog({
  initialText,
  summary,
  sentLine,
  note = null,
  pending,
  error,
  onCancel,
  onSave,
  onMarkSent,
}: {
  initialText: string;
  summary: ItEmailSummary;
  /** Set on a sent email ("Sent … by …, N still waiting"); null on a draft. */
  sentLine: string | null;
  /** Shown in place of the summary on a draft with nothing left to move (a checklist's email). */
  note?: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (text: string) => void;
  onMarkSent: (text: string) => void;
}) {
  const sent = sentLine !== null;
  const [text, setText] = useState(initialText);
  const [editing, setEditing] = useState(false);

  function copy() {
    void (async () => {
      try {
        await navigator.clipboard.writeText(text);
        showToast(STR.itEmails.copied);
      } catch {
        // Clipboard refused: the text is on screen and selectable (Edit text gives a box).
        showToast(STR.itEmails.copyFailed, 'warn');
      }
    })();
  }

  const summaryLine = (
    <div style={{ fontSize: 12.5 }}>
      {STR.itEmails.summaryHead(summary.items, summary.systems)}{' '}
      <b className={IT_TONE.setup.className}>{STR.itEmails.toSetUp(summary.setUp)}</b>
      {STR.itEmails.summaryJoin}
      <b className={IT_TONE.remove.className}>{STR.itEmails.toRemove(summary.remove)}</b>.
      {summary.shared > 0 ? ` ${STR.itEmails.summaryShared(summary.shared)}` : null}
    </div>
  );

  return (
    <Modal
      title={sent ? STR.itEmails.sentTitle : STR.itEmails.draftTitle}
      lead={STR.itEmails.draftLead}
      width="min(760px,100%)"
      dismissable={!pending}
      onClose={onCancel}
      footer={
        sent ? (
          <>
            <button type="button" className="ghost" style={modalButtonStyle} onClick={onCancel}>
              {STR.common.close}
            </button>
            <button type="button" className="prim" style={modalButtonStyle} onClick={copy}>
              {STR.itEmails.copyText}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="ghost"
              style={modalButtonStyle}
              disabled={pending}
              onClick={onCancel}
            >
              {STR.common.cancel}
            </button>
            {summary.items > 0 ? (
              <button
                type="button"
                className="ghost"
                style={modalButtonStyle}
                disabled={pending}
                onClick={() => {
                  onSave(text);
                }}
              >
                {STR.itEmails.saveDraft}
              </button>
            ) : null}
            <button type="button" className="ghost" style={modalButtonStyle} onClick={copy}>
              {STR.itEmails.copyText}
            </button>
            {summary.items > 0 ? (
              <button
                type="button"
                className="prim"
                style={modalButtonStyle}
                disabled={pending || text.trim() === ''}
                onClick={() => {
                  onMarkSent(text);
                }}
              >
                {pending ? STR.itEmails.marking : STR.itEmails.markSent(summary.items)}
              </button>
            ) : null}
          </>
        )
      }
    >
      <div style={{ padding: '16px 22px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sent ? (
          <div style={{ fontSize: 12.5 }}>{sentLine}</div>
        ) : note === null ? (
          summaryLine
        ) : (
          <div style={{ fontSize: 12.5 }}>{note}</div>
        )}
        <ToneLegend />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ ...fieldLabelStyle, margin: 0 }}>{STR.itEmails.emailText}</span>
            {sent ? null : (
              <button
                type="button"
                className="tbtn"
                style={{ ...rowActionStyle, marginLeft: 'auto' }}
                onClick={() => {
                  setEditing((value) => !value);
                }}
              >
                {editing ? STR.itEmails.doneEditing : STR.itEmails.editText}
              </button>
            )}
          </div>
          {editing && !sent ? (
            <textarea
              className="field"
              value={text}
              onChange={(event) => {
                setText(event.target.value);
              }}
              rows={16}
              aria-label={STR.itEmails.emailText}
              autoFocus
              style={{
                ...monoStyle,
                width: '100%',
                boxSizing: 'border-box',
                padding: '9px 11px',
                borderRadius: 8,
                fontSize: 12,
                lineHeight: 1.55,
                resize: 'vertical',
              }}
            />
          ) : (
            <ColouredEmail text={text} />
          )}
        </div>
        <span className="tx-subtle" style={hintStyle}>
          {editing && !sent ? `${STR.itEmails.editingHint} ` : null}
          {STR.itEmails.sendHint}
        </span>
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}
      </div>
    </Modal>
  );
}
